import { datadogRum } from '@datadog/browser-rum';
import axios from 'axios';
import { enqueueSnackbar } from 'notistack';

import { localViews } from 'modules/Auth/AuthProvider/views';

/** Requests the backend proxies to the bookkeeping provider (Tight/Hurdlr). */
const BOOKKEEPING_V2_PREFIX = 'bookkeeping/v2/';

/** `ProxyTokenError` — a 502 carrying no `details.reason`, so its body is the only key. */
const PROXY_TOKEN_ERROR = 'Failed to acquire proxy token';

/**
 * `details.reason` values on a 424 that mean the provider refused a credential we held
 * (`CredentialErrorReason`, hyke/bookkeeping/v2/exceptions.py).
 *
 * `not_linked` shares the 424 and is excluded: it means the member never connected a bookkeeping
 * account, which is fixed by provisioning rather than by telling an operator.
 */
const REJECTED_REASONS = ['rejected_past_expiry', 'rejected_before_expiry'];

/** `ProxyTokenError` — the mint call itself failed, which may be a transient upstream fault. */
const MINT_FAILED_MESSAGE =
    "Couldn't open this member's books — the bookkeeping connection failed. Your Collective session is still active; reload to retry.";

/** `BookkeepingCredentialRejected` — the provider refused the credential we hold. */
const REJECTED_MESSAGE =
    'The bookkeeping connection for this member was rejected. Your Collective session is still active.';

/**
 * A credential failure fails every bookkeeping request on the page at once, so the notice is
 * latched. A time window rather than a boolean because nothing clears it — unlike
 * `forceLogoutRedirect`'s latch, there is no navigation. Latched per message so the two failures
 * cannot swallow each other.
 */
const NOTICE_WINDOW_MS = 60_000;

const lastNoticeAt = new Map<string, number>();

/** The message this failure warrants, or null when it is not a bookkeeping credential failure. */
function messageFor(status: number | undefined, data: unknown): string | null {
    const body = data as { error?: unknown; details?: { reason?: unknown } } | undefined;

    // 502 is shared with the generic upstream failure, so the body has to match as well.
    if (status === 502 && body?.error === PROXY_TOKEN_ERROR) {
        return MINT_FAILED_MESSAGE;
    }

    if (status === 424 && REJECTED_REASONS.includes(String(body?.details?.reason))) {
        return REJECTED_MESSAGE;
    }

    // Anything else falls through to no notice: a false "your bookkeeping broke" is worse than none.
    return null;
}

/**
 * Tell an operator that the bookkeeping dependency, not their session, is what failed (AP-2577).
 *
 * One notice for the whole v2 surface rather than a branch in each of the ~18 request modules,
 * none of which shows an operator anything actionable today.
 *
 * Members are not notified: they reach bookkeeping on their own `OAuthCredential`, never a proxy
 * token, so the failure is not theirs and the wording would not fit.
 */
export function bookkeepingCredentialErrorNotice(error: unknown): void {
    if (!axios.isAxiosError(error) || !error.config?.url?.includes(BOOKKEEPING_V2_PREFIX)) {
        return;
    }

    const message = messageFor(error.response?.status, error.response?.data);
    if (!message || !localViews.isEmployee()) {
        return;
    }

    const now = Date.now();
    const previous = lastNoticeAt.get(message);
    if (previous !== undefined && now - previous < NOTICE_WINDOW_MS) {
        return;
    }
    lastNoticeAt.set(message, now);

    datadogRum.addAction('bookkeeping_credential_notice', {
        status: error.response?.status,
        url: error.config.url,
    });

    enqueueSnackbar(message, { variant: 'warning', autoHideDuration: 10000 });
}

// Exported for testing only
export function resetBookkeepingCredentialNotice(): void {
    lastNoticeAt.clear();
}
